"use client";

import { useEffect, useState } from "react";
import { api, loadTokens } from "./api";
import { useRegion } from "./regionContext";

/**
 * EPIC-029 BR-primary display flip Story 3 (2026-05-08) — resolved
 * "which currency leads on every screen" hook. Distinct from
 * `useDisplayCurrency`: that hook decides which currency the values
 * are FORMATTED in (USDC vs BRL vs USD); this one decides which side
 * is the PRIMARY render in dual-currency contexts.
 *
 * Resolution order:
 *   1. **Hard rule (CLAUDE.md 2026-05-08):** when the CURRENT region is
 *      not brazil, return 'USDC' regardless of persisted preference.
 *      Non-BR users see NO BRL projection anywhere — full stop. A user
 *      who signed up while their region was 'brazil' (and got
 *      `display_currency_primary='BRL'` backfilled by migration 0143)
 *      and later switched to a non-BR region MUST see USDC primary
 *      while in the non-BR region. The persisted preference only
 *      applies inside region=brazil.
 *   2. `users.display_currency_primary` from /v1/me when authed
 *      (per-user override; only respected when region=brazil).
 *   3. `granbay.display_currency_primary` cookie from a recent
 *      Story 6 toggle (mirror set on flip so SSR honours the new
 *      pref before the /me round-trip; only respected when
 *      region=brazil).
 *   4. region-derived default: region=brazil → 'BRL', else 'USDC'.
 *
 * Used by TradePanel + receipts/history surfaces (Stories 3-4) to
 * pick which side leads in `formatMoneyPrimary({primary, secondary})`
 * outputs. Anonymous viewers get the region-derived default with no
 * network round-trip.
 *
 * 2026-05-11 founder report — leo2 (signed up BR-region, currently
 * viewing US-region) saw "R$131,50 (≈ $26.30 USDC)" in the close-
 * position modal. Persisted preference was sticky across region
 * change; hard rule forces USDC inside non-BR regions to fix.
 */
export function useDisplayCurrencyPrimary(): "USDC" | "BRL" {
  const region = useRegion();
  const isBrazilianRegion = region.region === "brazil";
  const regionDefault = isBrazilianRegion
    ? ("BRL" as const)
    : ("USDC" as const);

  // 2026-05-09 — start with the region-derived default so SSR + the
  // first client render produce identical output. React #418 hydration
  // mismatch otherwise: server has no document.cookie + returns
  // regionDefault; client first render reads the cookie and may return
  // a different value, tearing the SSR'd subtree.
  //
  // The cookie + /me values land via the useEffect below, after
  // hydration, so the resolved value still updates without re-fetching
  // anything that the SSR had right.
  const [resolved, setResolved] = useState<"USDC" | "BRL">(regionDefault);

  useEffect(() => {
    // Outside region=brazil, the hard rule fixes USDC; skip the cookie +
    // /me lookups entirely. Re-runs whenever the region changes so a
    // BR→US flip clears any stale BRL state from the previous render.
    if (!isBrazilianRegion) {
      setResolved("USDC");
      return;
    }

    // Cookie precedence — mirror set by Story 6 toggle so the very
    // first effect after hydration honours the new value before the
    // /me round-trip lands.
    if (typeof document !== "undefined") {
      const cookie = readCookie("granbay.display_currency_primary");
      if (cookie === "USDC" || cookie === "BRL") {
        setResolved(cookie);
      }
    }

    let cancelled = false;
    if (!loadTokens()) return;
    void api
      .me()
      .then((profile) => {
        if (cancelled) return;
        // display_currency_primary lands on develop alongside this hook
        // via Story 1's column + Story 6's typing on the Me record.
        // Cast through unknown for the cross-PR window where the field
        // is on the API response but not yet on the in-tree type.
        const v = (profile as unknown as Record<string, unknown>)
          .display_currency_primary;
        if (v === "USDC" || v === "BRL") setResolved(v);
      })
      .catch(() => {
        // Non-fatal — keep the cookie/region-derived value.
      });
    return () => {
      cancelled = true;
    };
  }, [isBrazilianRegion]);

  // Final hard-rule guard: even if a stale `resolved='BRL'` slipped
  // through from a prior brazilian-region render before the effect
  // re-ran, never return BRL outside region=brazil.
  if (!isBrazilianRegion) return "USDC";
  return resolved;
}

function readCookie(name: string): string | null {
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)"),
  );
  return m ? decodeURIComponent(m[1]) : null;
}
